import { type FulfillmentApi } from '@entities/fulfillment'

export type CatalogSearchParams = {
  query?: string
  city?: string
  /** Пусто — фильтра нет. Несколько типов — фулфилмент должен работать со всеми сразу. */
  shipmentTypes: FulfillmentApi.ShipmentType[]
  /** Пусто — фильтра нет. Несколько площадок — фулфилмент должен отгружать на все сразу. */
  marketplaces: FulfillmentApi.Marketplace[]
  /** Возит на маркетплейс не реже выбранного; пусто — частота неважна. */
  deliveryFrequency?: FulfillmentApi.DeliveryFrequency
  sort: FulfillmentApi.CatalogSort
  page: number
  cityQuery?: string
}

const shipmentTypes = new Set<FulfillmentApi.ShipmentType>(['fbs', 'fbo', 'dbs', 'other'])
const marketplaces = new Set<FulfillmentApi.Marketplace>([
  'wildberries',
  'ozon',
  'yandex_market',
  'magnit_market',
  'other',
])
const catalogSorts = new Set<FulfillmentApi.CatalogSort>(['newest', 'rating'])
const deliveryFrequencies = new Set<FulfillmentApi.DeliveryFrequency>([
  'twice_a_day',
  'once_a_day',
  'several_times_a_week',
  'rarely',
])
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const first = (value: string | string[] | undefined) => (Array.isArray(value) ? value[0] : value)

/**
 * Разбирает строку адреса так же, как её видит сервер: повторённый ключ остаётся списком, из
 * которого дальше берётся первое значение. Обычное `Object.fromEntries` оставило бы последнее —
 * и по ссылке вида `?marketplace=wildberries&marketplace=ozon` страница показывала бы одно, а
 * первый же клик по фильтру молча переключал бы на другое.
 */
export const searchParamsFromUrl = (search: string): Record<string, string | string[]> => {
  const parsed = new URLSearchParams(search)

  return Object.fromEntries(Array.from(parsed.keys(), (key) => [key, parsed.getAll(key)]))
}

const text = (value: string | string[] | undefined) => {
  const normalized = first(value)?.trim()
  return normalized ? normalized.slice(0, 160) : undefined
}

/** Значения приходят через запятую; чужое и повторы отбрасываем. */
const toKnownList = <T extends string>(value: string | string[] | undefined, known: Set<T>) => {
  const requested = (first(value) ?? '').split(',') as T[]

  return Array.from(new Set(requested.filter((item) => known.has(item))))
}

export const toCatalogSearchParams = (
  searchParams: Record<string, string | string[] | undefined>
): CatalogSearchParams => {
  const requestedPage = Number(first(searchParams.page) ?? 1)
  const city = text(searchParams.city)

  const requestedSort = first(searchParams.sort) as FulfillmentApi.CatalogSort | undefined
  const requestedFrequency = first(searchParams.delivery_frequency) as FulfillmentApi.DeliveryFrequency | undefined
  return {
    query: text(searchParams.query),
    city: city && uuidPattern.test(city) ? city : undefined,
    shipmentTypes: toKnownList(searchParams.shipment_type, shipmentTypes),
    marketplaces: toKnownList(searchParams.marketplace, marketplaces),
    deliveryFrequency:
      requestedFrequency && deliveryFrequencies.has(requestedFrequency) ? requestedFrequency : undefined,
    sort: requestedSort && catalogSorts.has(requestedSort) ? requestedSort : 'newest',
    page: Number.isInteger(requestedPage) && requestedPage >= 1 ? requestedPage : 1,
    cityQuery: text(searchParams.city_query),
  }
}

export const toQueryString = (
  params: CatalogSearchParams,
  changes: Partial<CatalogSearchParams & { page: number | undefined }>
) => {
  const next = { ...params, ...changes }
  const query = new URLSearchParams()

  if (next.query) {
    query.set('query', next.query)
  }
  if (next.city) {
    query.set('city', next.city)
  }
  if (next.shipmentTypes.length) {
    query.set('shipment_type', next.shipmentTypes.join(','))
  }
  if (next.marketplaces.length) {
    query.set('marketplace', next.marketplaces.join(','))
  }
  if (next.deliveryFrequency) {
    query.set('delivery_frequency', next.deliveryFrequency)
  }
  if (next.sort !== 'newest') {
    query.set('sort', next.sort)
  }
  if (next.page && next.page > 1) {
    query.set('page', String(next.page))
  }
  if (next.cityQuery) {
    query.set('city_query', next.cityQuery)
  }
  const value = query.toString()
  return value ? `/fulfillment?${value}` : '/fulfillment'
}
